import { Box, Flex, Separator, Text } from '@chakra-ui/react'
import React from 'react'
import { LuArrowLeft, LuChevronRight } from 'react-icons/lu'

import { useColorModeValue } from '@/components/ui/color-mode'
import { Tooltip } from '@/components/ui/tooltip'
import { Link, useLocation } from '@/routing'

import type { NavRail, NavSection } from './nav.types'
import { resolveActiveEntry } from './navUtils'
import { RailEntry } from './RailEntry'

/**
 * The rail's contents: a way back in, the sections, and the door out at the foot.
 *
 * The whole redesign is visible in what is *absent* — there is no second level here, and no
 * horizontal bar above the content. A section that would need children is a mode, reached by
 * the door, which replaces this rail rather than adding a row to it.
 *
 * The three bands are laid out as a column with only the middle one scrolling: the settings
 * rail lists every object one administers and is routinely taller than the viewport, and a
 * door that scrolls away is a door nobody finds.
 */
export const ContextRail = ({
  rail,
  collapsed = false,
}: {
  rail: NavRail
  collapsed?: boolean
}) => {
  const location = useLocation()
  const active = resolveActiveEntry(rail.sections, location.pathname)

  const doorBorder = useColorModeValue('gray.200', 'gray.700')
  const doorActiveBg = useColorModeValue('purple.100', 'purple.900')
  const backBg = useColorModeValue('gray.50', 'gray.800')

  const doorIsActive = rail.door
    ? location.pathname.split('/').includes('settings')
    : false

  return (
    <Flex direction="column" h="full" minH={0}>
      {rail.backLink ? (
        <Box flexShrink={0}>
          <Link to={rail.backLink.to} style={{ textDecoration: 'none' }}>
            <Flex
              align="center"
              gap={2.5}
              mx={collapsed ? 2 : 3}
              mb={2}
              px={collapsed ? 2 : 3}
              py={2.5}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={doorBorder}
              bg={backBg}
              justify={collapsed ? 'center' : 'flex-start'}
              _hover={{ borderColor: 'brand.400' }}
            >
              <LuArrowLeft size={15} />
              {collapsed ? null : (
                <Box minW={0}>
                  <Text fontSize="sm" fontWeight="semibold" truncate>
                    {rail.backLink.label}
                  </Text>
                </Box>
              )}
            </Flex>
          </Link>
        </Box>
      ) : null}

      <Box flex="1" minH={0} overflowY="auto" overflowX="hidden">
        {rail.sections.map((section, index) => (
          <RailSection
            key={section.key}
            section={section}
            first={index === 0}
            collapsed={collapsed}
            activeKey={active?.key}
          />
        ))}
      </Box>

      {rail.door ? (
        <Box pt={3} flexShrink={0}>
          <Tooltip
            content={rail.door.label}
            positioning={{ placement: 'right' }}
            disabled={!collapsed}
          >
            <Link to={rail.door.to} style={{ textDecoration: 'none' }}>
              <Flex
                align="center"
                gap={3}
                mx={collapsed ? 2 : 3}
                px={collapsed ? 3 : 4}
                py={2.5}
                minH="40px"
                borderRadius="lg"
                borderWidth="1px"
                borderColor={doorIsActive ? 'purple.400' : doorBorder}
                bg={doorIsActive ? doorActiveBg : undefined}
                justify={collapsed ? 'center' : 'flex-start'}
                _hover={{ borderColor: 'purple.400' }}
              >
                {rail.door.icon ? <rail.door.icon size={17} /> : null}
                {collapsed ? null : (
                  <>
                    <Text fontSize="sm" fontWeight="semibold" flex="1" truncate>
                      {rail.door.label}
                    </Text>
                    <LuChevronRight size={14} />
                  </>
                )}
              </Flex>
            </Link>
          </Tooltip>
        </Box>
      ) : null}
    </Flex>
  )
}

/**
 * One block of the rail: an optional caption, then its entries.
 *
 * Nothing folds here any more. The settings rail used to list every object one administers,
 * folded, until it turned out that a rail full of other objects' settings is a directory, not a
 * navigation — the way to another network is the switcher at the rail's head.
 */
const RailSection = ({
  section,
  first,
  collapsed,
  activeKey,
}: {
  section: NavSection
  first: boolean
  collapsed: boolean
  activeKey?: string
}) => (
  <Box>
    {first || !collapsed ? null : <Separator my={2} mx={3} />}

    {section.label && !collapsed ? (
      <Text
        px={5}
        pt={first ? 2 : 4}
        pb={1.5}
        fontSize="10px"
        fontWeight="bold"
        letterSpacing="0.1em"
        textTransform="uppercase"
        color="fg.subtle"
        truncate
      >
        {section.label}
      </Text>
    ) : null}

    {section.entries.map((entry) => (
      <RailEntry
        key={entry.key}
        entry={entry}
        active={entry.key === activeKey}
        collapsed={collapsed}
      />
    ))}
  </Box>
)
